import Box from "@mui/material/Box";
import BasicModal from "@/components/basic-modal";
import { useFormik } from "formik";
import UITextField from "@/components/ui-controls/textfield";
import UISelect from "@/components/ui-controls/select";
import UISwitch from "@/components/ui-controls/ui-switch";
import { InitialValues, formSchema } from "./form-schema";
import DynamicFieldWrapper from "./dynamic-field";
import { Typography } from "@mui/material";
import dropdownEnums, { Enums } from "@/services/dropdown-enums";
import {
  arrayToCommaSeparatedString,
  getCustomFieldEnumKey,
  getFieldValidationByType,
  getFieldsLimitInfoByType,
} from "../helper";
import UseGql from "@/services/graph-ql/useGql";
import { Get_Custom_field_Quota } from "@/modules/crm/leads/graphql-queries/custom-field";
import { useState } from "react";
import GeneralSkeleton from "@/components/skeleton/general-skeleton";
import { toTitleCase } from "@/services/messaging/message.methods";

const fieldTypeLimit = 10;

const createSubTypeOptions = (type) => {
  let includeNone = true;
  let options = getSubTypes(type, includeNone);
  return options.map((subType) => ({
    label: handleSubTypeNone(subType, type),
    value: subType?.value,
  }));
};

const getSubTypes = (type, includeNone = false) => {
  let subTypes = dropdownEnums.enum_Custom_Field_Sub_Type.filter(
    (opt) =>
      opt?.parentType.includes(type) && (opt?.value !== 0 || includeNone),
  );
  return subTypes;
};

const getSubTypesTextList = (type) => {
  let subTypes = dropdownEnums.enum_Custom_Field_Sub_Type
    .filter((opt) => opt?.parentType.includes(type.value))
    .map((item) => handleSubTypeNone(item, type.value));
  return arrayToCommaSeparatedString(subTypes);
};

const handleSubTypeNone = (subType, parentType) => {
  return subType.value === Enums.CustomFieldSubType.None &&
    (parentType === Enums.CustomFieldType.Text ||
      parentType === Enums.CustomFieldType.Numeric)
    ? getCustomFieldEnumKey(parentType, "value") + " Field"
    : subType.key;
};

const FieldModal = ({
  open,
  onClose,
  title = "Add field in lead form",
  onSave,
  loading,
  data,
}) => {
  const getFieldsLimit = UseGql(Get_Custom_field_Quota);
  const [fieldsInfo, setFieldsInfo] = useState({});
  const noSubTypes = [
    Enums.CustomFieldType.SingleValue,
    Enums.CustomFieldType.MultiValue,
  ];

  // get fields limit and count info
  const getLimit = () => {
    getFieldsLimit.executeQuery().then((res) => {
      if (
        res &&
        res?.data &&
        res?.data?.customFieldQuota &&
        res?.data?.customFieldQuota.items
      ) {
        let data = res?.data?.customFieldQuota?.items;
        let fieldsLimitInfo = getFieldsLimitInfoByType(data);
        setFieldsInfo(fieldsLimitInfo);
      }
    });
  };

  const onOpenModal = () => {
    getLimit();
    if (data && Object.keys(data).length) {
      let savedData = {
        fieldName: data?.displayName,
        isImportant: data?.isImportant,
        description: data?.description,
        fieldSubType: data?.fieldSubType,
        fieldType: data?.fieldType,
      };
      if (data?.leadCustomOptions && data?.leadCustomOptions.length) {
        savedData = {
          ...savedData,
          leadCustomOptions: data?.leadCustomOptions,
        };
      }
      fieldForm.setValues({ ...fieldForm.values, ...savedData });
    }
  };

  const handleFormSubmit = (value) => {
    console.log("onsubmit value", value);
    if (data && Object.keys(data).length) {
      value = { ...value, id: data?.id };
    }
    if (value?.leadCustomOptions?.length) {
      value = {
        ...value,
        leadCustomOptions: value.leadCustomOptions.map(
          ({ _rowId, value: optionValue, id }) => ({
            value: optionValue,
            ...(id != null ? { id } : {}),
          })
        ),
      };
    }
    let validation = getFieldValidationByType(value);
    if (validation) {
      value = { ...value, data: validation };
    }
    console.log("validation value", value);
    onSave({ ...value, fieldName: value.fieldName.trim() });
  };

  const fieldForm = useFormik({
    enableReinitialize: true,
    initialValues: InitialValues,
    validationSchema: formSchema,
    onSubmit: handleFormSubmit,
  });

  const handleModalClose = () => {
    fieldForm.resetForm();
    onClose();
  };

  const handleTypeChange = (e) => {
    let value = e.target.value;
    fieldForm.setFieldValue("fieldSubType", "");
    if (
      value !== Enums.CustomFieldType.SingleValue ||
      value !== Enums.CustomFieldType.MultiValue
    ) {
      fieldForm.setFieldValue("leadCustomOptions", [{ value: "" }]);
    }
    if (noSubTypes.includes(value)) {
      // Set first sub type as default value
      let defaultOption = getSubTypes(value, true)[0]?.value;
      fieldForm.setFieldValue("fieldSubType", defaultOption);
    }
  };

  const createOptions = (types = []) => {
    let options = types.map((type) => {
      let childs = getSubTypesTextList(type);
      let countInfo = fieldsInfo[type?.value];
      let helperText = (
        <Box
          className="d-flex justify-content-space-between"
          sx={{
            width: "100%",
            fontSize: "0.85rem",
            letterSpacing: "0.2px",
            fontWeight: "300",
          }}
        >
          {noSubTypes.includes(type?.value) ? (
            <span>{childs}</span>
          ) : (
            <span>Data type(s) include {childs} &nbsp;</span>
          )}
          <span className="text-right">
            {countInfo?.fieldCount} /{countInfo?.limit}
          </span>
        </Box>
      );
      return {
        label: type?.key,
        value: type?.value,
        helperText: helperText,
        disabled: !!(countInfo?.fieldCount === countInfo?.limit),
      };
    });
    return options;
  };

  const canAddOptions = (type) => {
    let includedTypes = [
      Enums.CustomFieldType.SingleValue,
      Enums.CustomFieldType.MultiValue,
    ];
    return includedTypes.includes(type);
  };

  const isValidNumberOrEmpty = (value) => {
    if (value === "") {
      return false;
    }
    const numericValue = Number(value);
    return !isNaN(numericValue) && numericValue >= 0;
  };
  return (
    <BasicModal
      open={open}
      title={toTitleCase(title)}
      handleClose={handleModalClose}
      size="sm"
      saveButtonTitle="Save"
      saveCallback={() => fieldForm.handleSubmit()}
      saveBtnLoading={loading}
      modalBodySx={{ paddingTop: "2rem", paddingBottom: "1rem" }}
      onEnter={onOpenModal}
      onExit={handleModalClose}
    >
      {getFieldsLimit?.loading ? (
        <Box sx={{ display: "grid", gap: 4 }}>
          <GeneralSkeleton sx={{ transform: "scale(1)", height: "34px" }} />
          <GeneralSkeleton sx={{ transform: "scale(1)", height: "34px" }} />
          {isValidNumberOrEmpty(fieldForm?.values?.fieldSubType) && (
            <GeneralSkeleton sx={{ transform: "scale(1)", height: "38px" }} />
          )}
          {(fieldForm.values?.fieldType == Enums.CustomFieldType.SingleValue ||
            fieldForm.values?.fieldType ==
              Enums.CustomFieldType.MultiValue) && (
            <>
              <Box>
                <GeneralSkeleton
                  sx={{
                    transform: "scale(1)",
                    marginBottom: "5px",
                    marginTop: "-5px",
                    height: "9px",
                    width: "0",
                  }}
                />
                <Box sx={{ display: "grid", gap: 4 }}>
                  {fieldForm.values?.leadCustomOptions?.map((item) => (
                    <GeneralSkeleton
                      key={item}
                      sx={{ transform: "scale(1)", height: "38px" }}
                    />
                  ))}
                </Box>
              </Box>
            </>
          )}
          <GeneralSkeleton sx={{ transform: "scale(1)", height: "71px" }} />
          <GeneralSkeleton
            sx={{ transform: "scale(1)", height: "34px", width: "40%" }}
          />
        </Box>
      ) : (
        <Box sx={{ display: "grid", gap: 4 }}>
          <UITextField
            type={"text"}
            name={"fieldName"}
            value={fieldForm.values.fieldName}
            title={"Field Name"}
            placeholder={"Field Name"}
            onChange={fieldForm.handleChange}
            onBlur={fieldForm?.handleBlur}
            // tooltip={tooltip}
            // prefix={prefix}
            error={
              (fieldForm.touched["fieldName"] &&
                fieldForm.errors["fieldName"]) ||
              false
            }
            maxLength={250}
            // disabled={disableFields}
          />
          <UISelect
            name="fieldType"
            value={fieldForm.values.fieldType}
            title="Field Type"
            onChange={(e) => {
              handleTypeChange(e);
              fieldForm.handleChange(e);
            }}
            options={createOptions(dropdownEnums.enum_Custom_Field_Type)}
            clearable={false}
            disabled={data?.id ? true : false}
            error={
              (fieldForm.touched["fieldType"] &&
                fieldForm.errors["fieldType"]) ||
              false
            }
            // menuItemSx={{
            //   display: "flex",
            //   alignItems: "baseline",
            //   width: "100%",
            // }}
          />
          {fieldForm?.values.fieldType &&
            !noSubTypes.includes(fieldForm?.values.fieldType) && (
              <UISelect
                name="fieldSubType"
                value={fieldForm.values.fieldSubType}
                title="Field SubType"
                onChange={fieldForm.handleChange}
                options={createSubTypeOptions(fieldForm.values.fieldType)}
                clearable={false}
                disabled={data?.id ? true : false}
                error={
                  (fieldForm.touched["fieldSubType"] &&
                    fieldForm.errors["fieldSubType"]) ||
                  false
                }
              />
            )}

          {canAddOptions(fieldForm.values.fieldType) && (
            <Box>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Options (Required)
              </Typography>
              <DynamicFieldWrapper
                form={fieldForm}
                fieldKey="leadCustomOptions"
                title="Option"
              />
            </Box>
          )}

          <UITextField
            type={"text"}
            name={"description"}
            value={fieldForm.values.description}
            title={"Description (optional)"}
            // placeholder={"Description (optional)"}
            onChange={fieldForm.handleChange}
            multiline={true}
            rows={3}
            maxLength={1000}
            // onBlur={FormikForm?.handleBlur}
            // tooltip={tooltip}
            // prefix={prefix}
            // error={error}
            // disabled={disableFields}
          />
          <Box>
            <UISwitch
              // disabled={data && recordsLength == 1}
              name="isImportant"
              checked={fieldForm.values.isImportant}
              label={"Important"}
              handleChange={fieldForm.handleChange}
              sx={{ ml: 1, mt: 0 }}
              defaultStyled={false}
            />
          </Box>
        </Box>
      )}
    </BasicModal>
  );
};

export default FieldModal;
